import type { Area } from 'react-easy-crop';

import type { CropHandle, ImageLayout, RectSelection } from './types';

export function createInitialSelection(
  layout: ImageLayout,
  aspect: number,
  minWidth: number,
  prev: RectSelection | null,
): RectSelection {
  const maxWidth = layout.width;
  const maxHeight = layout.height;
  let width = prev?.width ?? Math.min(maxWidth * 0.92, maxHeight * aspect * 0.85);
  let height = width / aspect;
  if (height > maxHeight) {
    height = maxHeight * 0.85;
    width = height * aspect;
  }
  width = clamp(width, Math.min(minWidth, maxWidth), maxWidth);
  height = width / aspect;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspect;
  }
  const x =
    prev != null
      ? clamp(prev.x, layout.left, layout.left + layout.width - width)
      : layout.left + (layout.width - width) / 2;
  const y =
    prev != null
      ? clamp(prev.y, layout.top, layout.top + layout.height - height)
      : layout.top + (layout.height - height) / 2;
  return { x, y, width, height };
}

export function resizeRectSelection({
  origin,
  handle,
  dx,
  dy,
  layout,
  aspect,
  minWidth,
}: {
  origin: RectSelection;
  handle: CropHandle;
  dx: number;
  dy: number;
  layout: ImageLayout;
  aspect: number;
  minWidth: number;
}): RectSelection {
  const maxRight = layout.left + layout.width;
  const maxBottom = layout.top + layout.height;

  let widthDelta = 0;
  switch (handle) {
    case 'e':
    case 'ne':
    case 'se':
      widthDelta = dx;
      break;
    case 'w':
    case 'nw':
    case 'sw':
      widthDelta = -dx;
      break;
    case 'n':
      widthDelta = -dy * aspect;
      break;
    case 's':
      widthDelta = dy * aspect;
      break;
  }

  let width = clamp(origin.width + widthDelta, minWidth, layout.width);
  let height = width / aspect;
  if (height > layout.height) {
    height = layout.height;
    width = height * aspect;
  }

  let x = origin.x;
  let y = origin.y;

  if (handle.includes('w')) {
    x = origin.x + origin.width - width;
  }
  if (handle.includes('n')) {
    y = origin.y + origin.height - height;
  }
  if (handle === 'n' || handle === 's') {
    x = origin.x + (origin.width - width) / 2;
  }
  if (handle === 'e' || handle === 'w') {
    y = origin.y + (origin.height - height) / 2;
  }

  x = clamp(x, layout.left, maxRight - width);
  y = clamp(y, layout.top, maxBottom - height);

  return { x, y, width, height };
}

export function mapSelectionToRotatedPixels({
  selection,
  layout,
  rotation,
}: {
  selection: RectSelection;
  layout: ImageLayout;
  rotation: number;
}): Area {
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const relX = (selection.x - layout.left) / layout.scale;
  const relY = (selection.y - layout.top) / layout.scale;
  const relW = selection.width / layout.scale;
  const relH = selection.height / layout.scale;

  if (normalizedRotation === 0) {
    return { x: relX, y: relY, width: relW, height: relH };
  }

  const { width: boundsW, height: boundsH } = getRotatedBounds(
    layout.naturalWidth,
    layout.naturalHeight,
    normalizedRotation,
  );

  return {
    x: clamp(relX, 0, Math.max(0, boundsW - relW)),
    y: clamp(relY, 0, Math.max(0, boundsH - relH)),
    width: relW,
    height: relH,
  };
}

export function getRotatedBounds(
  width: number,
  height: number,
  rotation: number,
): {
  width: number;
  height: number;
} {
  const rotRad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Failed to load image')));
    if (!src.startsWith('blob:')) {
      image.crossOrigin = 'anonymous';
    }
    image.src = src;
  });
}
